import React from "react";
import { Link } from "react-router-dom";
import {
  FaInfoCircle,
  FaUserCircle,
  FaCode,
  FaCog,
  FaExclamationCircle,
} from "react-icons/fa";
import "../index.css";
import CodeBlock from "./CodeBlock";
import SignatureExample from "./SignatureExample";

const API = "https://saas.todoai.ai/api";

const FLOW = `1. Design the page once in Avatar Studio (saved to your account).
2. Your backend calls POST /api/avatar/embed/session and gets a url.
3. Send the user to that url: redirect, new tab, iframe or WebView.
4. The user talks, types or uses the camera on the hosted page.
5. When the session ends, the user returns to your return page.`;

const LANG_REQUEST = `curl -X GET ${API}/agents/supported-languages/`;

const LANG_RESPONSE = `{
  "details": [
    { "code": "eng", "name": "English" },
    { "code": "it", "name": "Italian" },
    { "code": "fr", "name": "French" },
    { "code": "es", "name": "Spanish" },
    { "code": "de", "name": "German" },
    { "code": "pt", "name": "Portuguese" },
    { "code": "ar", "name": "Arabic" },
    { "code": "zh-CN", "name": "Chinese (Simplified)" },
    { "code": "pl", "name": "Polish" }
  ],
  "count": 9
}`;

const LIST_REQUEST = `curl -X GET ${API}/avatar/list \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE"`;

const LIST_RESPONSE = `{
  "avatars": [
    { "id": "AVATAR_ID_1", "ethnicity": "AVATAR_LABEL_1" },
    { "id": "AVATAR_ID_2", "ethnicity": "AVATAR_LABEL_2" }
  ]
}`;

const CREATE_REQUEST = `curl -X POST ${API}/avatar/embed/session \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -d '{
    "avatar_id": "YOUR_AVATAR_ID",
    "domain": "YOUR_DOMAIN",
    "language": "it"
  }'`;

const CREATE_RESPONSE = `{
  "url": "https://avatar.example.com/embed/ONE_TIME_CODE/",
  "expires_in": 600
}`;

const REDIRECT_JS = `// Your backend returns the url from the create call. In the browser:
window.location.href = url;          // same tab
// or: window.open(url, "_blank");   // new tab`;

const IFRAME = `<iframe
  src="https://avatar.example.com/embed/ONE_TIME_CODE/"
  width="380"
  height="640"
  style="border:0; border-radius:16px"
  allow="microphone; camera; autoplay; fullscreen">
</iframe>`;

const Required = () => (
  <span className="docs-badge docs-badge-required">Required</span>
);
const Optional = () => <span className="docs-badge">Not Required</span>;

const RequestTable = ({ title, rows }) => (
  <div className="docs-table">
    <h4 className="docs-response-title">{title}</h4>
    <table>
      <thead>
        <tr>
          <th>Field</th>
          <th>Type</th>
          <th>Required</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, type, required, description]) => (
          <tr key={name}>
            <td>{name}</td>
            <td>
              <code>{type}</code>
            </td>
            <td>{required ? <Required /> : <Optional />}</td>
            <td>{description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SimpleTable = ({ title, head, rows }) => (
  <div className="docs-table">
    {title && <h4 className="docs-response-title">{title}</h4>}
    <table>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell, i) => (
              <td key={i}>{i === 0 ? <code>{cell}</code> : cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AvatarEmbed = () => {
  return (
    <div className="docs-page">
      <div className="docs-page-header">
        <h1 className="docs-page-title">Hosted Avatar</h1>
        <p className="docs-page-description">
          A ready-made TODO AI page where your users talk to a live video
          avatar. Your backend creates a link and sends the user there.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaUserCircle /> Overview
        </h2>
        <CodeBlock title="Flow" code={FLOW} />

        <div className="docs-alert docs-alert-info">
          <FaInfoCircle className="docs-alert-icon" />
          <div className="docs-alert-content">
            <div className="docs-alert-title">Design it in Avatar Studio</div>
            <p className="docs-alert-message">
              Layout, colors, controls and branding are saved to your account
              and applied to every session. You do not send them in the request.
            </p>
            <Link to="/avatar-studio" className="docs-studio-button">
              Open Avatar Studio
            </Link>
          </div>
        </div>

        <h3 className="docs-subsection-title">Authentication</h3>
        <p>
          Send <code>X-API-Key</code> and <code>X-Signature</code> from your
          backend. The signature is the HMAC-SHA256 (hex) of the{" "}
          <code>domain</code> value, or of an empty string for{" "}
          <code>/api/avatar/list</code>. See{" "}
          <a href="/docs/getting-started">Getting API Documentation</a>. Never
          put the secret key in browser or app code.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaUserCircle /> List Avatars
        </h2>
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method get">GET</span>
            <code className="docs-endpoint-url">/api/avatar/list</code>
          </div>
          <p className="docs-endpoint-description">
            Returns the avatars you can use as <code>avatar_id</code>.
          </p>
          <CodeBlock title="Example Request" code={LIST_REQUEST} />
          <CodeBlock title="200 OK Response" code={LIST_RESPONSE} />
        </div>
      </div>

      <div className="docs-section" id="languages">
        <h2 className="docs-section-title">
          <FaUserCircle /> Supported Languages
        </h2>
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method get">GET</span>
            <code className="docs-endpoint-url">
              /api/agents/supported-languages/
            </code>
          </div>
          <p className="docs-endpoint-description">
            Public, no authentication. Send a <code>code</code> from this list
            as <code>language</code> when you create a session.
          </p>
          <CodeBlock title="Example Request" code={LANG_REQUEST} />
          <CodeBlock title="200 OK Response" code={LANG_RESPONSE} />
        </div>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Create a Session
        </h2>
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method post">POST</span>
            <code className="docs-endpoint-url">/api/avatar/embed/session</code>
          </div>
          <div className="docs-endpoint-meta">
            <span className="docs-endpoint-auth">
              <FaInfoCircle /> Authentication Required
            </span>
          </div>
          <p className="docs-endpoint-description">
            Creates a single-use link to your avatar page. The avatar starts
            only when the user presses Start.
          </p>

          <RequestTable
            title="Request Body (JSON)"
            rows={[
              ["avatar_id", "string", true, "Id from /api/avatar/list."],
              [
                "domain",
                "string",
                true,
                "Your business domain, enabled for your account. Also the signature payload.",
              ],
              [
                "language",
                "string",
                false,
                "A code from /api/agents/supported-languages/. Default: English (en). Set per session.",
              ],
            ]}
          />
          <CodeBlock title="Example Request" code={CREATE_REQUEST} />
          <CodeBlock title="200 OK Response" code={CREATE_RESPONSE} />

          <SimpleTable
            title="Response Fields"
            head={["Field", "Description"]}
            rows={[
              [
                "url",
                "Link to the avatar page. The session can be started once from it, and an unopened link expires after expires_in seconds.",
              ],
              ["expires_in", "Seconds before an unopened link expires."],
            ]}
          />

          <SimpleTable
            title="Errors"
            head={["Status", "Meaning"]}
            rows={[
              [
                "400",
                "A field is missing or invalid, for example an unsupported language.",
              ],
              ["401", "Missing or invalid credentials or signature."],
              ["503", "The link could not be created. Retry shortly."],
            ]}
          />
          <p>
            Error format:{" "}
            <code>{`{ "error": { "code": "...", "message": "..." } }`}</code>
          </p>
        </div>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Open the Page
        </h2>
        <p>
          <strong>Redirect or new tab.</strong> The user returns to your return
          page when the session ends.
        </p>
        <CodeBlock title="JavaScript" code={REDIRECT_JS} />
        <p>
          <strong>iframe.</strong> Allow the microphone, camera, autoplay and
          fullscreen.
        </p>
        <CodeBlock title="HTML" code={IFRAME} />
        <p>
          <strong>Mobile apps.</strong> Load the link in a WebView.
        </p>
        <p>
          Want only the avatar inside your own interface? See{" "}
          <Link to="/docs/avatar/embedded">Embedded Avatar</Link>.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCog /> What the User Can Do
        </h2>
        <ul className="docs-list">
          <li>
            <strong>Type a message.</strong> Answered with{" "}
            <a href="/docs/agent/generate-response">
              Generate Conversational Response
            </a>
            .
          </li>
          <li>
            <strong>Speak.</strong> A voice message of up to 60 seconds.
          </li>
          <li>
            <strong>Face analysis.</strong> A video of up to 45 seconds, using{" "}
            <a href="/docs/agent/process-video">Process Video</a>.
          </li>
        </ul>
        <p>Answers count against your plan like normal API calls.</p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaExclamationCircle /> Notes
        </h2>
        <ul className="docs-list">
          <li>
            A session can be started once from a link. An unopened link
            expires after <code>expires_in</code> seconds.
          </li>
          <li>
            A refresh during a session reconnects to the same session. Closing
            the tab ends it after about 30 seconds.
          </li>
          <li>Sessions end automatically after a maximum duration.</li>
          <li>
            Set "Allowed sites" in Avatar Studio to limit which sites can embed
            the page.
          </li>
          <li>The return page must be an http(s) link or a mobile app link.</li>
          <li>
            Browsers play sound only after a tap, so the page has a Start
            button.
          </li>
        </ul>
      </div>

      <SignatureExample />
    </div>
  );
};

export default AvatarEmbed;
